import React, { useEffect, useRef, useState } from "react";
import {
  Box,
  Typography,
  Select,
  MenuItem,
  FormControl,
  CircularProgress,
  useTheme,
} from "@mui/material";
import { ChevronDownIcon, FileIcon, ProfileIcon } from "../../../assets/icon";
import { MessageSquare } from "lucide-react";
import { RealtimeMenuItem } from "@/types/realtimeSpeech";
import {
  fieldLabelStyles,
  dropdownFormControlStyles,
  dropdownSelectStyles,
  dropdownMenuItemStyles,
  dropdownValueRowStyles,
  getDropdownIconBoxStyles,
  dropdownValueTextStyles,
  dropdownChevronStyles,
  dropdownPlaceholderStyles,
  dropdownMenuItemTextStyles,
  dropdownMenuItemDescriptionStyles,
  getDropdownMenuPaperStyles,
  getPersonaAvatarFallbackStyles,
} from "./styles";
import { AuthenticatedAvatar } from "../../authenticated-media/authenticated-avatar";

export type RoleplayFieldType = "domain" | "persona" | "scenario" | "default";

interface RoleplayDropdownFieldProps {
  label: string;
  fieldType: RoleplayFieldType;
  options: RealtimeMenuItem[];
  value: string;
  placeholder?: string;
  disabled?: boolean;
  loading?: boolean;
  onChange: (option: RealtimeMenuItem) => void;
}

const getOptionAvatar = (option: RealtimeMenuItem): string | null =>
  option.avatar ||
  option.avatar_url ||
  option.avatarUrl ||
  option.image ||
  option.image_url ||
  option.imageUrl ||
  null;

const FieldIcon: React.FC<{
  fieldType: RoleplayFieldType;
  option?: RealtimeMenuItem | null;
}> = ({ fieldType, option }) => {
  const theme = useTheme();

  if (fieldType === "persona") {
    const avatar = option ? getOptionAvatar(option) : null;
    if (avatar) {
      return <AuthenticatedAvatar src={avatar} sx={{ width: 28, height: 28 }} />;
    }
    return (
      <Box sx={getPersonaAvatarFallbackStyles(theme)}>
        <ProfileIcon size={16} />
      </Box>
    );
  }

  if (fieldType === "scenario") {
    return (
      <Box sx={getDropdownIconBoxStyles(theme)}>
        <MessageSquare size={16} />
      </Box>
    );
  }

  return (
    <Box sx={getDropdownIconBoxStyles(theme)}>
      <FileIcon size={16} />
    </Box>
  );
};

const isOptionDisabled = (text: string) =>
  text === "6IB" || text === "Clinical Messaging";

export const RoleplayDropdownField: React.FC<RoleplayDropdownFieldProps> = ({
  label,
  fieldType,
  options,
  value,
  placeholder = "Select an option",
  disabled = false,
  loading = false,
  onChange,
}) => {
  const theme = useTheme();
  const selectedOption = options.find((opt) => opt.text === value) ?? null;

  const fieldRef = useRef<HTMLDivElement | null>(null);
  const [fieldWidth, setFieldWidth] = useState<number | undefined>(undefined);

  useEffect(() => {
    const el = fieldRef.current;
    if (!el) return;
    const update = () => setFieldWidth(el.getBoundingClientRect().width);
    update();
    const ro = new ResizeObserver(update);
    ro.observe(el);
    return () => ro.disconnect();
  }, []);

  return (
    <Box sx={{ width: "100%" }}>
      <Typography sx={fieldLabelStyles}>{label}</Typography>
      <FormControl
        fullWidth
        ref={fieldRef}
        disabled={disabled || loading}
        sx={dropdownFormControlStyles}
      >
        <Select
          value={value || ""}
          displayEmpty
          IconComponent={() => null}
          renderValue={() => (
            <Box sx={dropdownValueRowStyles}>
              {loading ? (
                <CircularProgress size={18} sx={{ color: "#7B49C1" }} />
              ) : value ? (
                <>
                  <FieldIcon fieldType={fieldType} option={selectedOption} />
                  <Typography sx={dropdownValueTextStyles}>{value}</Typography>
                </>
              ) : (
                <Typography sx={dropdownPlaceholderStyles}>
                  {placeholder}
                </Typography>
              )}
              <ChevronDownIcon style={dropdownChevronStyles(theme)} />
            </Box>
          )}
          sx={dropdownSelectStyles}
          MenuProps={{
            anchorOrigin: { vertical: "bottom", horizontal: "left" },
            transformOrigin: { vertical: "top", horizontal: "left" },
            PaperProps: {
              sx: {
                ...getDropdownMenuPaperStyles(theme),
                ...(fieldWidth
                  ? { width: fieldWidth, minWidth: fieldWidth }
                  : {}),
              },
            },
          }}
          onChange={(e) => {
            const option = options.find((opt) => opt.text === e.target.value);
            if (option && !isOptionDisabled(option.text)) {
              onChange(option);
            }
          }}
        >
          {options.map((option) => (
            <MenuItem
              key={option.text}
              value={option.text}
              disabled={isOptionDisabled(option.text)}
              sx={dropdownMenuItemStyles}
            >
              <Box
                sx={{
                  display: "flex",
                  alignItems: "flex-start",
                  gap: 1.25,
                  width: "100%",
                }}
              >
                <FieldIcon fieldType={fieldType} option={option} />
                <Box sx={{ minWidth: 0, flex: 1 }}>
                  <Typography sx={dropdownMenuItemTextStyles}>
                    {option.text}
                  </Typography>
                  {option.description ? (
                    <Typography sx={dropdownMenuItemDescriptionStyles}>
                      {option.description.replace(/<[^>]+>/g, " ")}
                    </Typography>
                  ) : null}
                </Box>
              </Box>
            </MenuItem>
          ))}
        </Select>
      </FormControl>
    </Box>
  );
};
